import { z } from 'zod';
import { PositiveDecimalString, NonNegativeDecimalString, PaginationQuerySchema } from './common.js';

/**
 * Donation schemas.
 *
 * Donations are always tied to a DonationCause row. The user picks a cause,
 * the asset is determined by the cause (not the user), and the user supplies
 * an amount + optional note. Donation events are immutable — there is no
 * update or delete route, only create + read.
 *
 * Donation levels are derived from lifetime-donated totals (see
 * `DONATION_LEVEL_THRESHOLDS` below). The legacy system stored the current
 * level on the User row, but we recompute from the Donation table at read
 * time so the source of truth is never ambiguous.
 */

/** Assets accepted for donations. */
export const DONATION_ASSETS = ['uo', 'ep'] as const;
export type DonationAsset = (typeof DONATION_ASSETS)[number];

/**
 * Donation level thresholds in cumulative UO donated.
 * Level 0 = none, 1 = 好心人, 2 = 爱心志愿者, … up to 13 = 圣光者.
 * These match the 14 levels the legacy platform used.
 */
export const DONATION_LEVEL_THRESHOLDS: ReadonlyArray<{ level: number; min_uo: string; label_zh: string }> = [
  { level: 0, min_uo: '0', label_zh: '暂无' },
  { level: 1, min_uo: '10', label_zh: '好心人' },
  { level: 2, min_uo: '50', label_zh: '爱心志愿者' },
  { level: 3, min_uo: '100', label_zh: '爱心使者' },
  { level: 4, min_uo: '500', label_zh: '公益之星' },
  { level: 5, min_uo: '1000', label_zh: '慈善大使' },
  { level: 6, min_uo: '5000', label_zh: '慈善先锋' },
  { level: 7, min_uo: '10000', label_zh: '慈善之光' },
  { level: 8, min_uo: '50000', label_zh: '慈善功勋' },
  { level: 9, min_uo: '100000', label_zh: '慈善守护者' },
  { level: 10, min_uo: '500000', label_zh: '慈善至尊' },
  { level: 11, min_uo: '1000000', label_zh: '慈善传奇' },
  { level: 12, min_uo: '5000000', label_zh: '慈善之神' },
  { level: 13, min_uo: '10000000', label_zh: '圣光者' },
];

// ---- Cause admin CRUD -------------------------------------------------------

export const DonationCauseCreateSchema = z.object({
  code: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9_]+$/, 'must be lowercase alphanumeric + underscore'),
  title_zh: z.string().min(1).max(200),
  title_en: z.string().min(1).max(200),
  description_zh: z.string().min(1),
  description_en: z.string().min(1),
  image_url: z.string().url().max(1024).optional(),
  asset: z.enum(DONATION_ASSETS).default('uo'),
  goal_amount: NonNegativeDecimalString.default('0'),
  sort: z.coerce.number().int().default(0),
  is_active: z.coerce.boolean().default(true),
});
export type DonationCauseCreateRequest = z.infer<typeof DonationCauseCreateSchema>;

export const DonationCausePatchSchema = DonationCauseCreateSchema.partial().omit({ code: true });
export type DonationCausePatchRequest = z.infer<typeof DonationCausePatchSchema>;

export const DonationCauseListQuerySchema = PaginationQuerySchema.extend({
  is_active: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true')),
});
export type DonationCauseListQuery = z.infer<typeof DonationCauseListQuerySchema>;

// ---- User donation create ---------------------------------------------------

export const CreateDonationSchema = z.object({
  cause_id: z.coerce.number().int().positive(),
  amount: PositiveDecimalString,
  note: z.string().max(500).optional(),
});
export type CreateDonationRequest = z.infer<typeof CreateDonationSchema>;

// ---- Donation read queries --------------------------------------------------

export const DonationListQuerySchema = PaginationQuerySchema.extend({
  cause_id: z.coerce.number().int().positive().optional(),
  /** Admin-only: restrict to a specific donor id. Ignored for user calls. */
  donor_id: z.coerce.number().int().positive().optional(),
});
export type DonationListQuery = z.infer<typeof DonationListQuerySchema>;
